import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RefreshIcon, SearchIcon, CloseIcon } from './Icons';
import Pagination from './Pagination';
import CallButton from './CallButton';
import {
  getKnownAgents,
  normalizeNumber,
  getCustomerNumber,
  getCallStatus,
  localDateStr,
  isInternationalNumber,
} from './callUtils';

const DEFAULT_PAGE_SIZE = 50;
// Same reasoning as DailyOrders.js's AUTO_REFRESH_MS — checkouts convert to
// orders (and drop off this list) or get abandoned throughout the day, so
// poll rather than requiring a manual refresh.
const AUTO_REFRESH_MS = 10000;

// Newest abandoned checkout first, so agents call customers while the cart is
// still fresh. Checkouts with no checkout_created_at go to the bottom.
const sortCartsNewestFirst = (list) =>
  [...list].sort((a, b) => {
    const aTime = a.checkout_created_at ? new Date(a.checkout_created_at).getTime() : NaN;
    const bTime = b.checkout_created_at ? new Date(b.checkout_created_at).getTime() : NaN;
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return bTime - aTime;
  });

// Checkouts started on Shopify but never completed as an order — fed by
// Shopify's checkouts/create and checkouts/update webhooks (see POST
// /api/webhook/razorpay-abandoned-cart in server/index.js). Unlike Daily
// Orders this can't be a live fetch: Shopify only pushes checkout events,
// there's no endpoint to poll for them, so the server keeps its own table
// (server/ivr/db.js's shopify_abandoned_checkouts) and this page reads that.
// A checkout with no phone gives an agent nothing to call — Shopify still
// fires the webhook for these (e.g. a shopper who only picked a shipping
// method before leaving, or one whose phone shows up only later at the
// shipping step), so they're real rows, just not callable ones. Kept out of
// the main list and off to the side under the "No Contact Detail" card
// instead of cluttering the actual call list with rows no agent can act on.
const hasPhone = (cart) => Boolean(cart.phone);

// How many times this checkout's customer has been rung via click-to-call,
// and whether any of those attempts got picked up - same reasoning as
// DailyOrders.js's getOrderCallBucket, scoped to attempts made after this
// checkout started (not just "ever called this number") so an older,
// unrelated answered call from a previous checkout doesn't mark a brand-new
// abandonment as already handled.
// Click-to-call attempts grouped by customer number, built once per `calls`
// change instead of re-scanning the whole all-time call-history array for
// every single cart (getCartCallBucket used to do that per row - an O(carts
// x calls) scan that reran on every render, including ones triggered by an
// unrelated `calls` refresh over SSE, which is what was freezing the page
// as call history grew).
const buildClickAttemptIndex = (calls) => {
  const index = new Map();
  calls.forEach((call) => {
    if (!(call.direction || '').toLowerCase().includes('click')) return;
    const number = getCustomerNumber(call);
    if (!index.has(number)) index.set(number, []);
    index.get(number).push(call);
  });
  return index;
};

const getCartCallBucket = (cart, clickAttemptsByNumber) => {
  const customerNumber = normalizeNumber(cart.phone);
  const checkoutAt = cart.checkout_created_at ? new Date(cart.checkout_created_at).getTime() : NaN;
  const attempts = (clickAttemptsByNumber.get(customerNumber) || []).filter((call) => {
    if (Number.isNaN(checkoutAt)) return true;
    const calledAt = new Date((call.start_stamp || '').replace(' ', 'T')).getTime();
    return Number.isNaN(calledAt) || calledAt >= checkoutAt;
  });
  if (attempts.some((call) => getCallStatus(call) === 'Answered')) return 'Answered';
  if (attempts.length > 0) return 'NotReceived';
  return 'NotCalled';
};

// Same customer often abandons a checkout more than once in quick succession
// (re-opens the same cart, tweaks something, leaves again) - two checkouts
// from the same phone number within this window are treated as one call, not
// two, so agents aren't asked to dial the same person twice for what's
// really the same abandonment. A gap wider than this is treated as a
// genuinely separate abandonment and both show up. A checkout an agent has
// already called (Answered or Not Received - anything but NotCalled) is
// never collapsed away regardless of the window, so it stays trackable.
const DUPLICATE_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

// Collapses repeat checkouts from the same phone number that land within
// DUPLICATE_WINDOW_MS of each other, keeping only the most recent checkout
// in each cluster (the older one's cart is presumed stale/superseded). Once
// the gap to the next-older checkout from that phone exceeds the window, that
// checkout starts a new cluster of its own and is kept too - so a customer
// who abandons a cart, then abandons another one four days later, still gets
// both rows.
const dedupeByPhone = (list, clickAttemptsByNumber) => {
  const groups = new Map();
  list.forEach((cart) => {
    const key = normalizeNumber(cart.phone);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(cart);
  });

  const kept = [];
  groups.forEach((group) => {
    const sorted = [...group].sort((a, b) => {
      const aTime = a.checkout_created_at ? new Date(a.checkout_created_at).getTime() : 0;
      const bTime = b.checkout_created_at ? new Date(b.checkout_created_at).getTime() : 0;
      return bTime - aTime; // newest first
    });
    let lastKeptTime = null;
    sorted.forEach((cart) => {
      const time = cart.checkout_created_at ? new Date(cart.checkout_created_at).getTime() : NaN;
      const alreadyCalled = getCartCallBucket(cart, clickAttemptsByNumber) !== 'NotCalled';
      if (
        alreadyCalled ||
        lastKeptTime === null ||
        Number.isNaN(time) ||
        lastKeptTime - time > DUPLICATE_WINDOW_MS
      ) {
        kept.push(cart);
        if (!Number.isNaN(time)) lastKeptTime = time;
      }
    });
  });
  return kept;
};

// "Today" when actionLogDate is today, else a short "22 Aug" label - same
// helper/reasoning as DailyOrders.js's formatActionCardDate, shown on the
// Converted/Rejected stat cards so the count is never mistaken for an
// all-time total once a different date is picked.
const formatActionCardDate = (dateStr) => {
  if (dateStr === localDateStr(new Date())) return 'Today';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// Prompts for the order number a checkout became once an agent converts it
// by phone - there's no ERP round trip to pull this from (unlike Daily
// Orders' Confirm/Cancel), so the agent enters it by hand.
function ConvertCheckoutModal({ cart, onClose, onSubmit }) {
  const [orderNumber, setOrderNumber] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!orderNumber.trim()) {
      setError('Order number is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(orderNumber.trim());
    } catch (err) {
      setError(err.message || 'Failed to mark this checkout converted.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Mark checkout converted</h2>
            <p className="modal-subtitle">{cart.customer_name || cart.phone || cart.checkout_name}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Order number</span>
            <input
              type="text"
              value={orderNumber}
              onChange={(event) => setOrderNumber(event.target.value)}
              autoFocus
              required
            />
          </label>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="btn btn-primary login-submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Mark converted'}
          </button>
        </form>
      </div>
    </div>
  );
}

// A call list of Shopify checkouts abandoned before converting to an order,
// cross-checked live against the ERP so a checkout whose customer already
// ordered elsewhere drops off on its own (see server/ivr/routes.js) - plus,
// on top of that, an agent's own Convert/Not Converted resolution once
// they've actually called the customer (see CLAUDE.md's Abandoned Carts
// section for the full picture).
function AbandonedCarts({ calls }) {
  const { authFetch } = useAuth();
  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);
  // Rebuilt only when `calls` actually changes, not on every render (e.g.
  // opening the Convert modal, an action-count poll) - see
  // buildClickAttemptIndex above for why this matters.
  const clickAttemptsByNumber = useMemo(() => buildClickAttemptIndex(calls), [calls]);
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Defaults to 'NotCalled' - that's the actual work queue agents land on,
  // same reasoning as DailyOrders.js's callFilter default, not the full
  // mixed bag of everything already in progress. 'All' shows the whole
  // actionable call list; 'NotReceived'/'Answered' scope it to a
  // call-bucket; 'NoContact' shows the checkouts with no phone number;
  // 'Converted'/'Rejected' show the action log (see isActionView below).
  // Set by clicking the matching stat card below.
  const [viewFilter, setViewFilter] = useState('NotCalled');
  const [search, setSearch] = useState('');
  // Per-cart_token Convert/Not Converted state, keyed the same way as
  // DailyOrders.js's actionState: 'working' while the request is in flight,
  // or { error } if it failed. Success isn't tracked here - the row is just
  // removed from `carts` instead.
  const [actionState, setActionState] = useState({});
  // The checkout currently shown in the "enter order number" modal, or null.
  const [convertingCart, setConvertingCart] = useState(null);
  // Per-cart_token WhatsApp Greeting send state: { status: 'sending'|'sent'|'error', message? }
  const [greetingState, setGreetingState] = useState({});
  // Tally of Convert/Not Converted clicks on actionLogDate (today by
  // default) - same reasoning/shape as DailyOrders.js's actionCounts, backed
  // by the server's ivr_abandoned_cart_actions table instead of ivr_cod_actions.
  const [actionCounts, setActionCounts] = useState({ converted: 0, notConverted: 0 });
  const [actionLog, setActionLog] = useState([]);
  const [actionLogLoading, setActionLogLoading] = useState(false);
  const [actionLogError, setActionLogError] = useState('');
  const [actionLogDate, setActionLogDate] = useState(() => localDateStr(new Date()));

  // `silent` skips the full-page loading state for the background
  // auto-refresh poll, same reasoning as DailyOrders.js's fetchOrders.
  const fetchCarts = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await authFetch('/api/ivr/abandoned-carts');
      const body = await response.json();
      if (!response.ok) {
        if (!silent) setCarts([]);
        setError(body?.error || 'Failed to fetch abandoned carts.');
        return;
      }
      setError('');
      setCarts(sortCartsNewestFirst(Array.isArray(body?.carts) ? body.carts : []));
    } catch (err) {
      if (!silent) setCarts([]);
      setError(err.message || 'Failed to fetch abandoned carts.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchActionCounts = async (date) => {
    try {
      const response = await authFetch(`/api/ivr/abandoned-carts/action-counts?date=${encodeURIComponent(date)}`);
      const body = await response.json();
      if (response.ok) {
        setActionCounts({ converted: body?.converted || 0, notConverted: body?.notConverted || 0 });
      }
    } catch (err) {
      // Non-critical - the stat cards just keep showing the last known counts.
    }
  };

  const fetchActionLog = async (type, date) => {
    setActionLogLoading(true);
    setActionLogError('');
    try {
      const response = await authFetch(
        `/api/ivr/abandoned-carts/actions?type=${type}&date=${encodeURIComponent(date)}`
      );
      const body = await response.json();
      if (!response.ok) {
        setActionLog([]);
        setActionLogError(body?.error || 'Failed to fetch this list.');
        return;
      }
      setActionLog(Array.isArray(body?.actions) ? body.actions : []);
    } catch (err) {
      setActionLog([]);
      setActionLogError(err.message || 'Failed to fetch this list.');
    } finally {
      setActionLogLoading(false);
    }
  };

  useEffect(() => {
    fetchCarts();
    const interval = setInterval(() => fetchCarts({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchActionCounts(actionLogDate);
    const interval = setInterval(() => fetchActionCounts(actionLogDate), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [actionLogDate]);

  useEffect(() => {
    setPage(1);
  }, [carts.length, viewFilter, search]);

  const isActionView = viewFilter === 'Converted' || viewFilter === 'Rejected';

  useEffect(() => {
    if (viewFilter === 'Converted') fetchActionLog('converted', actionLogDate);
    else if (viewFilter === 'Rejected') fetchActionLog('not_converted', actionLogDate);
  }, [viewFilter, actionLogDate]);

  // Same "always jump back to today" reasoning as DailyOrders.js's
  // selectActionFilter.
  const selectActionFilter = (filter) => {
    setActionLogDate(localDateStr(new Date()));
    setViewFilter(filter);
  };

  // Memoized: this used to re-run (including the O(carts x calls) bucket
  // scan below) on every render, so any unrelated state change - an
  // action-count poll, opening the Convert modal, paging - would redo the
  // full dedup/bucket pass and could visibly freeze the tab while typing or
  // clicking. Now it only reruns when the carts or calls actually change.
  const callableCarts = useMemo(
    () => sortCartsNewestFirst(dedupeByPhone(carts.filter(hasPhone), clickAttemptsByNumber)),
    [carts, clickAttemptsByNumber]
  );
  const noContactCarts = useMemo(() => carts.filter((cart) => !hasPhone(cart)), [carts]);

  // Non-+91 numbers are split into their own "International" bucket, same
  // reasoning/pattern as IncomingCalls.js's internationalCalls/regularCalls
  // split - Tata click-to-call and the Not Called/Not Received/Answered
  // buckets below are all built around domestic numbers, so an
  // international one shouldn't be silently counted into (or drive) those.
  const regularCarts = useMemo(
    () => callableCarts.filter((cart) => !isInternationalNumber(cart.phone)),
    [callableCarts]
  );
  const internationalCarts = useMemo(
    () => callableCarts.filter((cart) => isInternationalNumber(cart.phone)),
    [callableCarts]
  );

  const cartBuckets = useMemo(
    () => regularCarts.map((cart) => getCartCallBucket(cart, clickAttemptsByNumber)),
    [regularCarts, clickAttemptsByNumber]
  );
  const bucketCounts = {
    NotCalled: cartBuckets.filter((bucket) => bucket === 'NotCalled').length,
    NotReceived: cartBuckets.filter((bucket) => bucket === 'NotReceived').length,
    Answered: cartBuckets.filter((bucket) => bucket === 'Answered').length,
  };

  const isBucketFilter = viewFilter === 'NotCalled' || viewFilter === 'NotReceived' || viewFilter === 'Answered';
  const filteredCarts = useMemo(
    () =>
      viewFilter === 'NoContact'
        ? noContactCarts
        : viewFilter === 'International'
        ? internationalCarts
        : isBucketFilter
        ? regularCarts.filter((cart, index) => cartBuckets[index] === viewFilter)
        : regularCarts,
    [viewFilter, isBucketFilter, noContactCarts, internationalCarts, regularCarts, cartBuckets]
  );

  // Search narrows the selected card's list only; the card counts stay as
  // they are. Digits also match ignoring spaces and +91, so "98765 43210"
  // finds "+919876543210".
  const activeRows = useMemo(() => {
    const rows = isActionView ? actionLog : filteredCarts;
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const termDigits = term.replace(/\D/g, '');
    const searchDigits = termDigits.length >= 3 && termDigits.length === term.replace(/[\s+()-]/g, '').length;
    return rows.filter((row) => {
      const fields = [
        row.checkout_name,
        row.customer_name,
        row.phone,
        row.email,
        row.order_number,
        row.agent,
        ...(row.line_items || []).map((item) => item.title),
      ].filter(Boolean).map(String);
      if (fields.some((field) => field.toLowerCase().includes(term))) return true;
      return searchDigits && Boolean(row.phone) && String(row.phone).replace(/\D/g, '').includes(termDigits);
    });
  }, [isActionView, actionLog, filteredCarts, search]);
  const totalPages = Math.max(1, Math.ceil(activeRows.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = activeRows.slice((page - 1) * pageSize, page * pageSize);

  // Names and quantities render as separate columns (one line per product,
  // same order in both), matching DailyOrders.js's renderProductNames/Qtys.
  const renderProductNames = (items) =>
    (items || []).length ? items.map((item, i) => <div key={i}>{item.title}</div>) : '-';

  const renderProductQtys = (items) =>
    (items || []).length ? items.map((item, i) => <div key={i}>{item.quantity ?? '-'}</div>) : '-';

  const formatPrice = (amount, currency) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '-';
    const formatted = value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return currency && currency !== 'INR' ? `${formatted} ${currency}` : `₹${formatted}`;
  };

  const formatCheckoutDate = (raw) => {
    if (!raw) return '-';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Fail closed, same reasoning as DailyOrders.js's handleCodAction: the
  // checkout only disappears from `carts` on a 200 from the server, never
  // optimistically.
  const handleConvert = async (cart, orderNumber) => {
    setActionState((prev) => ({ ...prev, [cart.cart_token]: { status: 'working' } }));
    const response = await authFetch(`/api/ivr/abandoned-carts/${encodeURIComponent(cart.cart_token)}/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderNumber }),
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const message = body?.error || 'Failed to mark this checkout converted.';
      setActionState((prev) => ({ ...prev, [cart.cart_token]: { status: 'error', message } }));
      throw new Error(message);
    }
    setCarts((prev) => prev.filter((c) => c.cart_token !== cart.cart_token));
    setActionCounts((prev) => ({ ...prev, converted: prev.converted + 1 }));
    setConvertingCart(null);
  };

  // Same fixed AiSensy template as CallHistoryModal.js's "WhatsApp Greeting"
  // button (POST /api/customers/:number/send-whatsapp).
  const handleSendGreeting = async (cart) => {
    const token = cart.cart_token;
    setGreetingState((prev) => ({ ...prev, [token]: { status: 'sending' } }));
    try {
      const response = await authFetch(
        `/api/customers/${encodeURIComponent(normalizeNumber(cart.phone))}/send-whatsapp`,
        { method: 'POST' }
      );
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Send failed');
      setGreetingState((prev) => ({ ...prev, [token]: { status: 'sent' } }));
    } catch (error) {
      setGreetingState((prev) => ({ ...prev, [token]: { status: 'error', message: error.message } }));
    }
  };

  const handleNotConvert = async (cart) => {
    if (!window.confirm(`Mark the checkout for ${cart.customer_name || cart.phone} as not converted?`)) return;
    setActionState((prev) => ({ ...prev, [cart.cart_token]: { status: 'working' } }));
    try {
      const response = await authFetch(
        `/api/ivr/abandoned-carts/${encodeURIComponent(cart.cart_token)}/not-convert`,
        { method: 'POST' }
      );
      const body = await response.json().catch(() => null);
      if (!response.ok) throw new Error(body?.error || 'Failed to mark this checkout not converted.');
      setCarts((prev) => prev.filter((c) => c.cart_token !== cart.cart_token));
      setActionCounts((prev) => ({ ...prev, notConverted: prev.notConverted + 1 }));
    } catch (error) {
      setActionState((prev) => ({
        ...prev,
        [cart.cart_token]: { status: 'error', message: error.message },
      }));
    }
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <button type="button" className="btn btn-secondary" onClick={() => fetchCarts()} disabled={loading}>
          <RefreshIcon width={14} height={14} />
          Refresh
        </button>
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, phone, email, checkout, product or order number"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search abandoned carts"
          />
        </div>
      </div>

      <div className="stat-cards">
        <button
          type="button"
          className={`stat-card ${viewFilter === 'All' ? 'active' : ''}`}
          onClick={() => setViewFilter('All')}
        >
          <div className="stat-value">{regularCarts.length}</div>
          <div className="stat-label">Abandoned Checkouts</div>
        </button>
        <button
          type="button"
          className={`stat-card international ${viewFilter === 'International' ? 'active' : ''}`}
          title="Checkouts with a non-+91 phone number — kept separate from the domestic call-bucket cards"
          onClick={() => setViewFilter('International')}
        >
          <div className="stat-value">{internationalCarts.length}</div>
          <div className="stat-label">International</div>
        </button>
        <button
          type="button"
          className={`stat-card ${viewFilter === 'NotCalled' ? 'active' : ''}`}
          onClick={() => setViewFilter('NotCalled')}
        >
          <div className="stat-value">{bucketCounts.NotCalled}</div>
          <div className="stat-label">Not Called</div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'NotReceived' ? 'active' : ''}`}
          onClick={() => setViewFilter('NotReceived')}
        >
          <div className="stat-value">{bucketCounts.NotReceived}</div>
          <div className="stat-label">Not Received</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${viewFilter === 'Answered' ? 'active' : ''}`}
          onClick={() => setViewFilter('Answered')}
        >
          <div className="stat-value">{bucketCounts.Answered}</div>
          <div className="stat-label">Answered</div>
        </button>
        <button
          type="button"
          className={`stat-card answered ${viewFilter === 'Converted' ? 'active' : ''}`}
          title={`Checkouts converted by phone on ${formatActionCardDate(actionLogDate)} - click to see the list`}
          onClick={() => selectActionFilter('Converted')}
        >
          <div className="stat-value">{actionCounts.converted}</div>
          <div className="stat-label">
            Converted <span className="stat-label-date">({formatActionCardDate(actionLogDate)})</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'Rejected' ? 'active' : ''}`}
          title={`Checkouts marked not converted on ${formatActionCardDate(actionLogDate)} - click to see the list`}
          onClick={() => selectActionFilter('Rejected')}
        >
          <div className="stat-value">{actionCounts.notConverted}</div>
          <div className="stat-label">
            Rejected <span className="stat-label-date">({formatActionCardDate(actionLogDate)})</span>
          </div>
        </button>
        <button
          type="button"
          className={`stat-card critical ${viewFilter === 'NoContact' ? 'active' : ''}`}
          title="Checkouts with no phone number captured — nothing to call"
          onClick={() => setViewFilter('NoContact')}
        >
          <div className="stat-value">{noContactCarts.length}</div>
          <div className="stat-label">No Contact Detail</div>
        </button>
      </div>

      {isActionView && (
        <div className="incoming-filters-bar">
          <label htmlFor="cart-action-log-date">{viewFilter === 'Converted' ? 'Converted on' : 'Rejected on'}</label>
          <input
            id="cart-action-log-date"
            type="date"
            className="date-filter-input"
            value={actionLogDate}
            max={localDateStr(new Date())}
            onChange={(e) => setActionLogDate(e.target.value)}
          />
        </div>
      )}

      {isActionView
        ? actionLogError && <div className="call-feedback call-feedback-error">{actionLogError}</div>
        : error && <div className="call-feedback call-feedback-error">{error}</div>}

      {isActionView ? (
        actionLogLoading ? (
          <div className="loading-state">Loading…</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Checkout</th>
                  <th>Customer</th>
                  <th>Order Number</th>
                  <th>Agent</th>
                  <th>Date/Time</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center' }}>
                      {actionLogError
                        ? 'Could not load this list.'
                        : search.trim()
                        ? 'No checkouts match this search.'
                        : `No checkouts ${viewFilter === 'Converted' ? 'converted' : 'marked not converted'} on ${actionLogDate}.`}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr key={`${row.checkout_name}-${row.created_at}-${index}`}>
                      <td className="table-cell-strong">{row.checkout_name || '-'}</td>
                      <td>{row.customer_name || row.phone || '-'}</td>
                      <td>{row.order_number || '-'}</td>
                      <td>{row.agent || '-'}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )
      ) : loading ? (
        <div className="loading-state">Loading…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Checkout</th>
                <th>Started At</th>
                <th>Customer Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Products</th>
                <th>Qty</th>
                <th>Total Price</th>
                <th>Call</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center' }}>
                    {error
                      ? 'Could not load abandoned carts.'
                      : search.trim()
                      ? 'No checkouts match this search.'
                      : viewFilter === 'NoContact'
                      ? 'No checkouts missing contact details — every abandoned checkout has a phone number.'
                      : viewFilter === 'International'
                      ? 'No international checkouts right now.'
                      : isBucketFilter
                      ? 'No checkouts match this filter.'
                      : 'No abandoned checkouts right now.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((cart) => {
                  const rowAction = actionState[cart.cart_token] || {};
                  const working = rowAction.status === 'working';
                  const hasCalled = getCartCallBucket(cart, clickAttemptsByNumber) !== 'NotCalled';
                  const greeting = greetingState[cart.cart_token] || {};
                  return (
                    <tr key={cart.cart_token}>
                      <td className="table-cell-strong">{cart.checkout_name || '-'}</td>
                      <td>{formatCheckoutDate(cart.checkout_created_at)}</td>
                      <td>{cart.customer_name || '-'}</td>
                      <td>{cart.phone || '-'}</td>
                      <td>{cart.email || '-'}</td>
                      <td>{renderProductNames(cart.line_items)}</td>
                      <td>{renderProductQtys(cart.line_items)}</td>
                      <td>{formatPrice(cart.total_price, cart.currency)}</td>
                      <td>
                        {cart.phone ? (
                          <CallButton
                            agents={knownAgents}
                            customerNumber={normalizeNumber(cart.phone)}
                            onCallTriggered={() => {}}
                          />
                        ) : (
                          <span title="No phone number captured for this checkout">-</span>
                        )}
                        {cart.phone && (
                          <div>
                            <button
                              type="button"
                              className="btn btn-secondary btn-small"
                              style={{ marginTop: 6 }}
                              disabled={greeting.status === 'sending'}
                              title="Sends a fixed WhatsApp greeting to this customer"
                              onClick={() => handleSendGreeting(cart)}
                            >
                              {greeting.status === 'sending' ? 'Sending…' : 'WhatsApp Greeting'}
                            </button>
                            {greeting.status === 'sent' && <div className="call-feedback call-feedback-ok">Sent</div>}
                            {greeting.status === 'error' && (
                              <div className="call-feedback call-feedback-error">{greeting.message}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="cod-actions">
                          <button
                            type="button"
                            className="btn btn-primary btn-small"
                            disabled={working || !hasCalled}
                            title={hasCalled ? undefined : 'Call the customer before marking this checkout converted'}
                            onClick={() => setConvertingCart(cart)}
                          >
                            {working ? 'Working…' : 'Convert'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger-outline btn-small"
                            disabled={working || !hasCalled}
                            title={hasCalled ? undefined : 'Call the customer before marking this checkout not converted'}
                            onClick={() => handleNotConvert(cart)}
                          >
                            Not Converted
                          </button>
                        </div>
                        {!hasCalled && <div className="call-feedback">Call the customer first</div>}
                        {rowAction.status === 'error' && (
                          <div className="call-feedback call-feedback-error">{rowAction.message}</div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={activeRows.length}
      />

      {convertingCart && (
        <ConvertCheckoutModal
          cart={convertingCart}
          onClose={() => setConvertingCart(null)}
          onSubmit={(orderNumber) => handleConvert(convertingCart, orderNumber)}
        />
      )}
    </>
  );
}

export default AbandonedCarts;
